<!-- Usage: import CommandCenterTable from './commandCenter/CommandCenterTable.vue'; -->
<template>
  <div class="cc-table-card">
    <div v-if="showHeader" class="cc-table-card-header">
      <div class="cc-table-card-title-wrap">
        <i v-if="icon" :class="icon" class="cc-table-card-icon"></i>
        <span class="cc-table-card-title">{{ title }}</span>
      </div>
      <div class="cc-table-card-count">{{ displayTotal }}</div>
    </div>

    <!-- Optional toolbar slot (search bars, dropdowns, etc.) -->
    <div v-if="$slots.toolbar" class="cc-table-toolbar-slot">
      <slot name="toolbar" />
    </div>

    <div class="cc-table-wrap">
      <table class="cc-table" :style="tableStyle">
        <!-- colgroup is the single source of truth for column widths -->
        <colgroup>
          <col v-if="selectable" style="width: 48px; min-width: 48px;" />
          <col v-for="col in normalizedColumns"
               :key="`col-${col.field}`"
               :style="colGroupStyle(col)" />
        </colgroup>

        <thead>
          <!-- ── Title / sort row ────────────────────────────────── -->
          <tr class="cc-title-row">
            <th v-if="selectable" class="cc-col-select" @click.stop>
              <label class="cc-checkbox-wrap">
                <input type="checkbox" :checked="allSelected" @change="$emit('toggle-all')" />
              </label>
            </th>
            <th v-for="col in normalizedColumns"
                :key="col.field"
                :class="[col.headerClass || '', col.sortable === false ? 'is-not-sortable' : 'is-sortable', 'cc-resizable-th']"
                @click="onSort(col)">
              <slot :name="`filterHeader:${col.field}`" :col="col" :sort="currentSort">
                <div class="cc-th-inner" :style="{ justifyContent: colAlign(col) }">
                  <span>{{ col.title }}</span>
                  <span class="cc-sort-icon">{{ sortIcon(col.field, col.sortable) }}</span>
                </div>
              </slot>
              <span class="cc-resize-handle"
                    @mousedown.stop.prevent="startResize($event, col.field)"></span>
            </th>
          </tr>

          <!-- ── Per-column filter row ───────────────────────────── -->
          <tr v-if="filterable" class="cc-filter-row">
            <th v-if="selectable" class="cc-filter-cell cc-col-select"></th>
            <th v-for="col in normalizedColumns"
                :key="`filter-${col.field}`"
                class="cc-filter-cell"
                @click.stop>
              <div v-if="col.filterable !== false" class="cc-filter-input-wrap">
                <!-- Date + Datetime both use type="date" for the filter —
                     closes immediately when a day is picked, no time fiddling needed.
                     Cell display still shows full datetime for datetime columns. -->
                <input v-if="isDateCol(col) || isDatetimeCol(col)"
                       class="cc-filter-input"
                       type="date"
                       :value="colFilters[col.field] || ''"
                       @change="onColFilterCommit(col.field, $event.target.value)" />
                <!-- Text — fires immediately with debounce -->
                <input v-else
                       class="cc-filter-input"
                       type="text"
                       :placeholder="col.filterPlaceholder || 'Filter…'"
                       :value="colFilters[col.field] || ''"
                       @input="onColFilterInput(col.field, $event.target.value)" />

                <button v-if="colFilters[col.field]"
                        class="cc-filter-clear"
                        title="Clear filter"
                        @click="clearColFilter(col.field)">
                  ✕
                </button>

                <!-- Operator picker -->
                <div class="cc-op-wrap">
                  <svg class="cc-op-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 3h14M3 8h10M6 13h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                  </svg>
                  <select class="cc-op-select"
                          :value="colOperators[col.field] || defaultOperator(col)"
                          @change="onOperatorChange(col.field, $event.target.value)">
                    <option v-for="op in operatorsForCol(col)"
                            :key="op.value"
                            :value="op.value">
                      {{ op.label }}
                    </option>
                  </select>
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="!pagedData.length">
            <td :colspan="normalizedColumns.length + (selectable ? 1 : 0)"
                class="cc-table-empty">
              {{ emptyText }}
            </td>
          </tr>
          <tr v-for="(row, idx) in pagedData"
              :key="row.id || row.task_id || row.__rowId"
              class="cc-table-row"
              :class="{ 'cc-row-even': idx % 2 === 0, 'cc-row-odd': idx % 2 !== 0 }"
              @click="onRowClick(row)">
            <td v-if="selectable" class="cc-col-select" @click.stop>
              <label class="cc-checkbox-wrap">
                <input type="checkbox" :checked="!!row[selectedField]" @change="$emit('toggle-selected', row)" />
              </label>
            </td>
            <td v-for="col in normalizedColumns"
                :key="`${row.id || row.task_id || row.__rowId}-${col.field}`"
                :class="col.cellClass || ''"
                :style="colCellStyle(col)">
              <template v-if="col.type === 'lockExtension'">
                <button class="cc-lock-ext-btn"
                        type="button"
                        @click.stop="onLockExtension(row, col)">
                  <i class="fa-duotone fa-lock-keyhole-open"></i>
                  Request Extension
                </button>
              </template>
              <template v-else-if="col.cell && $slots[col.cell]">
                <slot :name="col.cell" :dataItem="row" :data-item="row" />
              </template>
              <template v-else>
                {{ formatCell(col, row[col.field]) }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Numbered pager (numberedPager prop, used by the Connect skin on Pipeline / Applications): the Contacts
         table's footer. Same page / pageSize state and the same page-change event as the classic footer. -->
    <nav v-if="numberedPager" class="cc-npager" aria-label="Pages">
      <template v-if="totalPages > 1">
        <button type="button" class="cc-npg" :disabled="page === 1" aria-label="First page" @click="goToFirst">&laquo;</button>
        <button type="button" class="cc-npg" :disabled="page === 1" aria-label="Previous page" @click="prevPage">&lsaquo;</button>
        <button v-for="n in pageWindow" :key="n" type="button" class="cc-npg" :class="{ on: n === page }"
                :aria-current="n === page ? 'page' : null" @click="goToPage(n)">{{ n }}</button>
        <button type="button" class="cc-npg" :disabled="page === totalPages" aria-label="Next page" @click="nextPage">&rsaquo;</button>
        <button type="button" class="cc-npg" :disabled="page === totalPages" aria-label="Last page" @click="goToLast">&raquo;</button>
      </template>
      <select v-model.number="pageSize" class="cc-npg-size" aria-label="Rows per page">
        <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
      </select>
      <span class="cc-npg-sum">{{ pageStart }}–{{ pageEnd }} of {{ displayTotal }}</span>
    </nav>

    <!-- Footer: always visible, contains page nav + showing summary + rows-per-page -->
    <div v-else class="cc-pagination-bar">
      <div class="cc-pagination-info">
        <span class="cc-page-summary">
          Showing {{ pageStart }}–{{ pageEnd }} of {{ displayTotal }}
        </span>
        <label class="cc-page-size-wrap">
          <span>Rows</span>
          <select v-model.number="pageSize" class="cc-page-size-select">
            <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
          </select>
        </label>
      </div>
      <div v-if="totalPages > 1" class="cc-pagination-actions">
        <span class="cc-page-of">Page {{ page }} of {{ totalPages }}</span>
        <button class="cc-page-btn" @click="goToFirst" :disabled="page === 1">First</button>
        <button class="cc-page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
        <button class="cc-page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
        <button class="cc-page-btn" @click="goToLast" :disabled="page === totalPages">Last</button>
      </div>
    </div>
  </div>
</template>

<script>
  const TEXT_OPS = [
    { value: 'contains', label: '≈ Contains' },
    { value: 'startswith', label: '^ Starts with' },
    { value: 'eq', label: '= Equals' },
    { value: 'neq', label: '≠ Not equals' },
    { value: 'doesnotcontain', label: '∌ Excludes' },
  ]
  const NUM_OPS = [
    { value: 'eq', label: '= Equals' },
    { value: 'gte', label: '≥ ≥' },
    { value: 'lte', label: '≤ ≤' },
    { value: 'gt', label: '> >' },
    { value: 'lt', label: '< <' },
    { value: 'neq', label: '≠ ≠' },
  ]
  const DATE_OPS = [
    { value: 'eq', label: '= On' },
    { value: 'gte', label: '≥ On/After' },
    { value: 'lte', label: '≤ On/Before' },
    { value: 'gt', label: '> After' },
    { value: 'lt', label: '< Before' },
  ]

  const MIN_COL_WIDTH = 60

  /**
   * Parse a raw cell value or filter string into a JS Date, handling the formats
   * we actually see in this app:
   *
   *   "MM/DD/YYYY HH:mm:ss"  ← API format  e.g. "04/07/2026 13:29:30"
   *   "MM/DD/YYYY"           ← date-only variant
   *   "YYYY-MM-DD"           ← from <input type="date">
   *   "YYYY-MM-DDTHH:mm"     ← from <input type="datetime-local">
   *   ISO 8601 strings       ← fallback
   *   JS Date objects        ← passthrough
   *
   * Returns a Date, or an invalid Date (getTime() === NaN) on failure.
   */
  function parseDate(raw) {
    if (raw == null || raw === '') return new Date(NaN)
    if (raw instanceof Date) return raw

    const str = String(raw).trim()

    // "MM/DD/YYYY HH:mm:ss" or "MM/DD/YYYY HH:mm" or "MM/DD/YYYY"
    const mdyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/)
    if (mdyMatch) {
      const [, mo, dy, yr, hh = '0', mm = '0', ss = '0'] = mdyMatch
      return new Date(+yr, +mo - 1, +dy, +hh, +mm, +ss)
    }

    // "YYYY-MM-DD" (from <input type="date">) — parse as local midnight, NOT UTC
    const isoDateMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoDateMatch) {
      const [, yr, mo, dy] = isoDateMatch
      return new Date(+yr, +mo - 1, +dy)
    }

    // "YYYY-MM-DDTHH:mm" (from <input type="datetime-local">) — already local
    const isoLocalMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (isoLocalMatch) {
      const [, yr, mo, dy, hh, mm, ss = '0'] = isoLocalMatch
      return new Date(+yr, +mo - 1, +dy, +hh, +mm, +ss)
    }

    // ISO 8601 with timezone (e.g. "2026-04-07T13:29:30Z") — standard parse is fine
    return new Date(str)
  }

  /**
   * Convert a raw value to milliseconds for comparison.
   *
   * isDateOnly=true  → floor both sides to local midnight (day-granular compare)
   * isDateOnly=false → full millisecond precision
   */
  function toMs(raw, isDateOnly) {
    const d = parseDate(raw)
    if (isNaN(d.getTime())) return NaN
    if (isDateOnly) return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    return d.getTime()
  }

  export default {
    name: "CommandCenterTable",
    props: {
      title: { type: String, default: "Results" },
      icon: { type: String, default: "" },
      showHeader: { type: Boolean, default: true },
      columns: { type: Array, default: () => [] },
      data: { type: Array, default: () => [] },
      selectable: { type: Boolean, default: false },
      selectedField: { type: String, default: "selected" },
      allSelected: { type: Boolean, default: false },
      activeSort: { type: Array, default: () => [] },
      emptyText: { type: String, default: "No records found." },
      defaultPageSize: { type: Number, default: 25 },
      maxPageSize: { type: Number, default: 100 },

      serverSide: { type: Boolean, default: false },
      serverTotal: { type: Number, default: 0 },
      serverSkip: { type: Number, default: 0 },

      filterable: { type: Boolean, default: false },
      filterDebounce: { type: Number, default: 800 },
      // Contacts-style numbered pager instead of First / Prev / Next / Last. Off by default: other pages unchanged.
      numberedPager: { type: Boolean, default: false },
    },

    data() {
      return {
        localSort: [],
        page: 1,
        pageSize: 25,
        colFilters: {},
        colOperators: {},
        _filterDebounceId: null,

        // Resize
        colWidths: {},
        resizing: false,
      }
    },

    computed: {
      normalizedColumns() {
        return (this.columns || []).filter(x => x && x.field)
      },

      currentSort() {
        if (Array.isArray(this.activeSort) && this.activeSort.length) return this.activeSort[0]
        if (Array.isArray(this.localSort) && this.localSort.length) return this.localSort[0]
        return null
      },

      sortedData() {
        let rows = Array.isArray(this.data) ? [...this.data] : []

        if (this.serverSide) return rows

        if (this.filterable) {
          const active = Object.entries(this.colFilters).filter(([, v]) => v !== '' && v != null)
          if (active.length) {
            rows = rows.filter(row =>
              active.every(([field, val]) => {
                const col = this.normalizedColumns.find(c => c.field === field)
                const op = this.colOperators[field] || this.defaultOperator(col || {})
                return this.applyOperatorLocal(op, row[field], val, col)
              })
            )
          }
        }

        const cur = this.currentSort
        if (!cur || !cur.field) return rows
        const { field, dir } = cur
        rows.sort((a, b) => {
          const av = this.sortValue(a, field), bv = this.sortValue(b, field)
          if (av === bv) return 0
          if (av == null || av === "") return 1
          if (bv == null || bv === "") return -1
          if (typeof av === "number" && typeof bv === "number") return dir === "asc" ? av - bv : bv - av
          const r = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" })
          return dir === "asc" ? r : -r
        })
        return rows
      },

      displayTotal() {
        return this.serverSide ? this.serverTotal : this.sortedData.length
      },

      safePageSize() {
        const max = Math.min(this.maxPageSize || 100, 100)
        const size = Number(this.pageSize || this.defaultPageSize || 25)
        if (size < 1) return 25
        return size > max ? max : size
      },

      totalPages() { return Math.max(1, Math.ceil(this.displayTotal / this.safePageSize)) },
      // Up to five page numbers around the current page (numbered pager).
      pageWindow() {
        const last = this.totalPages
        const start = Math.max(1, Math.min(this.page - 2, last - 4))
        const out = []
        for (let n = start; n <= Math.min(last, start + 4); n++) out.push(n)
        return out
      },

      pagedData() {
        if (this.serverSide) return this.sortedData
        const start = (this.page - 1) * this.safePageSize
        return this.sortedData.slice(start, start + this.safePageSize)
      },

      pageStart() { return !this.displayTotal ? 0 : (this.page - 1) * this.safePageSize + 1 },
      pageEnd() { return Math.min(this.page * this.safePageSize, this.displayTotal) },

    pageSizeOptions() {
      return [10, 25, 50, 100].filter(x => x <= Math.min(this.maxPageSize || 100, 100))
    },

    /**
     * Sum of every column's desired width.
     *
     * `table-layout: fixed` IGNORES min-width on <col> — only `width` counts.
     * So a datetime column asking for min-width:185px used to get an equal
     * share of the container instead (~140px with 11 columns), which is too
     * narrow for the date filter input + operator button and got clipped by
     * `.cc-filter-input-wrap { overflow: hidden }`.
     *
     * Giving the table this as a min-width means: narrow container → the
     * table keeps its real width and `.cc-table-wrap` scrolls horizontally;
     * wide container → fixed layout distributes the slack as before.
     */
    tableMinWidth() {
      const total = this.normalizedColumns.reduce((sum, col) => sum + this.desiredColWidth(col), 0)
      return Math.max(600, total + (this.selectable ? 48 : 0))
    },

    tableStyle() {
      const s = { minWidth: `${this.tableMinWidth}px` }
      if (this.resizing) s.userSelect = 'none'
      return s
    },
  },

  watch: {
    defaultPageSize: {
      immediate: true,
      handler(val) {
        const max  = Math.min(this.maxPageSize || 100, 100)
        const next = Number(val || 25)
        this.pageSize = next > max ? max : next
      }
    },
    columns() { this.colWidths = {} },
    data()          { if (!this.serverSide) this.ensureValidPage() },
    sortedData()    { if (!this.serverSide) this.ensureValidPage() },
    pageSize()      { this.page = 1; this.emitPageChange() },
    activeSort:     { deep: true, handler() { this.page = 1 } },
    serverSkip(val) { if (val === 0) this.page = 1 },
    colFilters:     { deep: true, handler() { if (!this.serverSide) this.page = 1 } },
  },

    methods: {
      // ── Row / sort ────────────────────────────────────────────────────
      onLockExtension(row, col) {
        // If the column defines a custom handler, use it
        if (typeof col.onLockExtension === 'function') {
          col.onLockExtension(row)
          return
        }
        // Emit so parents can handle it too
        this.$emit('lock-extension', row)
        // Default: open the lock reprice URL the same way LoanPipelineCard does
        const loanId = row.id || row.loanId || row.loanid || row.f364
        const url = `https://swiftapp.ai/lock-extension-request${loanId ? `?loan_id=${encodeURIComponent(loanId)}` : ''}`
        window.open(url, '_blank')
      },
      onRowClick(row) { this.$emit("row-click", row) },
      onSort(col) {
        if (col.sortable === false || col.type === 'lockExtension') return
        const cur = this.currentSort
        const dir = cur && cur.field === col.field && cur.dir === "asc" ? "desc" : "asc"
        this.localSort = [{ field: col.field, dir }]
        this.page = 1
        this.$emit("sort", { field: col.field, dir })
      },

      sortIcon(field, sortable) {
        if (sortable === false) return ""
        const cur = this.currentSort
        if (!cur || cur.field !== field) return ""
        return cur.dir === "asc" ? "▲" : "▼"
      },

      // ── Column style helpers ──────────────────────────────────────────
      colAlign(col) {
        if (col.align === "right") return "flex-end"
        if (col.align === "center") return "center"
        return "flex-start"
      },

    /**
     * The width a column actually needs, in px. Single source of truth for
     * both the <colgroup> and the table's min-width.
     */
    desiredColWidth(col) {
      if (this.colWidths[col.field] != null) return this.colWidths[col.field]

      const px = v => {
        const n = typeof v === 'number' ? v : parseInt(v, 10)
        return Number.isFinite(n) ? n : null
      }

      if (col.width)    return px(col.width)    || 140
      if (col.minWidth) return px(col.minWidth) || 140

      // Icon-only columns (cell slot, no real data) — keep them tight
      if (col.sortable === false && col.filterable === false && col.cell) return 44
      if (col.type === 'lockExtension') return 160

      // Date/datetime: the filter row needs date input (130) + clear (18)
      // + operator button (24) + gaps + cell padding. 205 fits all of it.
      if (col.type === 'datetime') return this.filterable && col.filterable !== false ? 205 : 185
      if (col.type === 'date')     return this.filterable && col.filterable !== false ? 205 : 150

      if (col.type === 'number' || col.type === 'currency' || col.type === 'percent') return 105

      return 140
    },

    colGroupStyle(col) {
      if (this.colWidths[col.field] != null) {
        const w = `${this.colWidths[col.field]}px`
        return { width: w, minWidth: w }
      }

      const s = {}
      if (col.maxWidth) s.maxWidth = typeof col.maxWidth === "number" ? `${col.maxWidth}px` : col.maxWidth

      // `table-layout: fixed` only honours `width` on <col>, so always emit a
      // concrete width rather than min-width. Slack is still distributed by
      // the fixed-layout algorithm when the container is wider than the sum.
      const w = `${this.desiredColWidth(col)}px`
      s.width    = w
      s.minWidth = w

      // Icon / action columns should never stretch
      if (!col.width && ((col.sortable === false && col.filterable === false && col.cell) || col.type === 'lockExtension')) {
        s.maxWidth = w
      }
      return s
    },

      colHeaderStyle() { return {} },

      colCellStyle(col) {
        const s = { textAlign: col.align || "left" }
        if (col.padding) s.padding = typeof col.padding === "number" ? `${col.padding}px` : col.padding
        return s
      },

      // ── Column resize ─────────────────────────────────────────────────
      startResize(event, field) {
        const th = event.target.closest('th')
        if (!th) return

        const startX = event.clientX
        const startWidth = th.getBoundingClientRect().width

        this.colWidths = { ...this.colWidths, [field]: startWidth }
        this.resizing = true

        const overlay = document.createElement('div')
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;cursor:col-resize;'
        document.body.appendChild(overlay)

        let rafId = null

        const onMouseMove = (e) => {
          if (rafId !== null) return
          rafId = requestAnimationFrame(() => {
            rafId = null
            const newWidth = Math.max(MIN_COL_WIDTH, startWidth + (e.clientX - startX))
            this.colWidths = { ...this.colWidths, [field]: newWidth }
          })
        }

        const onMouseUp = () => {
          if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null }
          document.removeEventListener('mousemove', onMouseMove)
          document.removeEventListener('mouseup', onMouseUp)
          overlay.remove()
          this.resizing = false
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
      },

      // ── Operator helpers ──────────────────────────────────────────────
      isNumericCol(col) { return ['number', 'currency', 'percent'].includes(col?.type) },
      isDateCol(col) { return col?.type === 'date' },
      isDatetimeCol(col) { return col?.type === 'datetime' },

      operatorsForCol(col) {
        if (this.isDateCol(col) || this.isDatetimeCol(col)) return DATE_OPS
        if (this.isNumericCol(col)) return NUM_OPS
        return TEXT_OPS
      },

      defaultOperator(col) {
        if (col?.filterOperator) return col.filterOperator
        if (this.isDateCol(col) || this.isDatetimeCol(col) || this.isNumericCol(col)) return 'eq'
        return 'contains'
      },

      applyOperatorLocal(op, cellRaw, filterVal, col) {
        if (cellRaw == null) return false

        if (this.isDateCol(col) || this.isDatetimeCol(col)) {
          // Both date and datetime columns filter at day granularity —
          // the filter input is always type="date" so we always floor to midnight
          const cMs = toMs(cellRaw, true)
          const fMs = toMs(filterVal, true)
          if (isNaN(cMs) || isNaN(fMs)) return false
          if (op === 'eq') return cMs === fMs
          if (op === 'gte') return cMs >= fMs
          if (op === 'lte') return cMs <= fMs
          if (op === 'gt') return cMs > fMs
          if (op === 'lt') return cMs < fMs
          return false
        }

        if (this.isNumericCol(col)) {
          const cN = Number(String(cellRaw).replace(/[$,%]/g, '').replace(/,/g, ''))
          const fN = Number(filterVal)
          if (isNaN(cN) || isNaN(fN)) return false
          if (op === 'eq') return cN === fN
          if (op === 'gte') return cN >= fN
          if (op === 'lte') return cN <= fN
          if (op === 'gt') return cN > fN
          if (op === 'lt') return cN < fN
          if (op === 'neq') return cN !== fN
          return false
        }

        const c = String(cellRaw).toLowerCase(), v = String(filterVal).toLowerCase()
        if (op === 'contains') return c.includes(v)
        if (op === 'doesnotcontain') return !c.includes(v)
        if (op === 'startswith') return c.startsWith(v)
        if (op === 'eq') return c === v
        if (op === 'neq') return c !== v
        return c.includes(v)
      },

      // ── Cell formatter ────────────────────────────────────────────────
      formatCell(col, value) {
        if (value == null || value === "") return "—"
        const type = col.type

        if (type === "currency") {
          const n = Number(value)
          if (!Number.isFinite(n)) return String(value)
          return new Intl.NumberFormat("en-US", {
            style: "currency", currency: col.currency || "USD",
            minimumFractionDigits: col.decimals ?? 0, maximumFractionDigits: col.decimals ?? 0
          }).format(n)
        }
        if (type === "number") {
          const n = Number(value)
          if (!Number.isFinite(n)) return String(value)
          return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: col.decimals ?? 0, maximumFractionDigits: col.decimals ?? 0
          }).format(n)
        }
        if (type === "percent") {
          const n = Number(value)
          if (!Number.isFinite(n)) return String(value)
          return `${n.toFixed(col.decimals ?? 1)}%`
        }
        if (type === "date") {
          const d = parseDate(value)
          if (isNaN(d.getTime())) return String(value)
          const fmt = col.dateFormat || "short"
          if (fmt === "short") return d.toLocaleDateString(undefined, { month: "2-digit", day: "2-digit", year: "numeric" })
          if (fmt === "medium") return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })
          if (fmt === "long") return d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
          return d.toLocaleDateString()
        }
        if (type === "datetime") {
          const d = parseDate(value)
          if (isNaN(d.getTime())) return String(value)
          const fmt = col.dateFormat || "short"
          if (fmt === "short") return d.toLocaleString(undefined, { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
          if (fmt === "medium") return d.toLocaleString(undefined, { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })
          if (fmt === "long") return d.toLocaleString(undefined, { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short" })
          return d.toLocaleString()
        }
        if (type === "boolean") return value ? (col.trueLabel || "Yes") : (col.falseLabel || "No")

        // Suppress raw true/false from boolean fields with no explicit type —
        // these are usually checkbox/selected state columns that render via a slot
        if (typeof value === "boolean") return ""

        if (this.shouldAutoDate(col, value)) {
          const d = new Date(value)
          if (!isNaN(d.getTime())) return d.toLocaleDateString(undefined, { month: "2-digit", day: "2-digit", year: "numeric" })
        }
        return value
      },

      shouldAutoDate(col, value) {
        if (col.type) return false
        const field = String(col?.field || "").toLowerCase()
        const title = String(col?.title || "").toLowerCase()
        const looks = field.includes("date") || field === "created" || field === "updated" ||
          field === "submitted" || field.includes("followup") || title.includes("date") ||
          title.includes("created") || title.includes("follow up") || title.includes("submitted")
        if (!looks || value == null || value === "") return false
        if (value instanceof Date) return true
        const str = String(value).trim()
        return str ? !isNaN(new Date(str).getTime()) : false
      },

      sortValue(row, field) {
        const value = row?.[field]
        if (value == null || value === "") return null
        if (typeof value === "number") return value
        if (typeof value === "boolean") return value ? 1 : 0
        if (value instanceof Date) return value.getTime()
        if (typeof value === "string") {
          const t = value.trim()
          const n = Number(t.replace(/[$,%]/g, "").replace(/,/g, ""))
          if (!Number.isNaN(n) && t !== "") return n
          // Use parseDate so MM/DD/YYYY HH:mm:ss sorts correctly
          if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(t) || /^\d{4}-\d{2}-\d{2}/.test(t) || /T|GMT|UTC/.test(t)) {
            const d = parseDate(t)
            if (!isNaN(d.getTime())) return d.getTime()
          }
          return t.toLowerCase()
        }
        return value
      },

      displayValue(val) { return val == null || val === "" ? "—" : val },

      // ── Pagination ────────────────────────────────────────────────────
      emitPageChange() {
        if (this.serverSide) {
          this.$emit("page-change", { skip: (this.page - 1) * this.safePageSize, take: this.safePageSize })
        }
      },
      ensureValidPage() { if (this.page > this.totalPages) this.page = 1 },
      prevPage() { if (this.page > 1) { this.page--; this.emitPageChange() } },
      nextPage() { if (this.page < this.totalPages) { this.page++; this.emitPageChange() } },
      goToFirst() { this.page = 1; this.emitPageChange() },
      goToLast() { this.page = this.totalPages; this.emitPageChange() },
      goToPage(n) { if (n >= 1 && n <= this.totalPages && n !== this.page) { this.page = n; this.emitPageChange() } },

      // ── Column filter ─────────────────────────────────────────────────
      onColFilterInput(field, value) {
        this.colFilters = { ...this.colFilters, [field]: value }
        if (this._filterDebounceId) clearTimeout(this._filterDebounceId)
        this._filterDebounceId = setTimeout(() => { this.emitFilterChange() }, this.filterDebounce)
      },

      // For date/datetime inputs — fires immediately on change (picker close), no debounce needed
      onColFilterCommit(field, value) {
        if (this._filterDebounceId) clearTimeout(this._filterDebounceId)
        this.colFilters = { ...this.colFilters, [field]: value }
        this.emitFilterChange()
      },

      onOperatorChange(field, operator) {
        this.colOperators = { ...this.colOperators, [field]: operator }
        if (this.colFilters[field]) {
          if (this._filterDebounceId) clearTimeout(this._filterDebounceId)
          this._filterDebounceId = setTimeout(() => { this.emitFilterChange() }, this.filterDebounce)
        }
      },

      clearColFilter(field) {
        this.colFilters = { ...this.colFilters, [field]: '' }
        if (this._filterDebounceId) clearTimeout(this._filterDebounceId)
        this.emitFilterChange()
      },
      resetFilters() {
        this.colFilters = {};
        this.colOperators = {};
      },
      emitFilterChange() {
        const filters = Object.entries(this.colFilters)
          .filter(([, v]) => v !== '' && v != null)
          .map(([field, value]) => {
            const col = this.normalizedColumns.find(c => c.field === field)
            const operator = this.colOperators[field] || this.defaultOperator(col || {})

            const filter = { field, operator, value }

            // Tell the backend this is a date so it casts correctly
            if (this.isDateCol(col) || this.isDatetimeCol(col)) {
              filter.type = 'date'
              filter.filter = 'date'
            }

            return filter
          })
        this.$emit('filter-change', { filters })
        if (this.serverSide) {
          this.page = 1
          this.emitPageChange()
        }
      },
    }
  }
</script>

<style scoped>
  /* ── Numbered pager (numberedPager) — the Contacts table's footer ── */
  .cc-npager { flex: 0 0 auto; display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px; padding: 12px; border-top: 1px solid #eef0f3; background: #fff; }
  .cc-npg { width: 34px; height: 34px; border-radius: 50%; border: 0; background: transparent; font: inherit; font-size: 15px; font-weight: 500; color: #4b5260; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
  .cc-npg:hover:not(:disabled):not(.on) { background: #f0f2f5; }
  .cc-npg.on { background: var(--primary, #051d3a); color: #fff; }
  .cc-npg:disabled { opacity: .35; cursor: default; }
  .cc-npg-size { margin-left: 12px; height: 34px; border: 1px solid #e6e8ec; border-radius: 8px; padding: 0 8px; font: inherit; font-size: 13px; font-weight: 500; color: #111827; background: #fff; }
  .cc-npg-sum { margin-left: 10px; font-size: 12px; font-weight: 500; color: #6b7280; }
  .cc-table-card {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .cc-table-card-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid #e9edf3;
    background: #fafbfd;
  }

  .cc-table-card-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cc-table-card-icon {
    font-size: 14px;
    color: #6f7b92;
  }

  .cc-table-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .cc-table-card-count {
    font-size: 13px;
    font-weight: 700;
    color: #7b8397;
  }

  .cc-table-toolbar-slot {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid #e9edf3;
    background: #f7f8fb;
  }

  .cc-table-wrap {
    flex: 1 1 0;
    min-height: 0;
    overflow: auto;
  }

  .cc-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 600px;
  }

    .cc-table th,
    .cc-table td {
      padding: 12px;
      border-bottom: 1px solid #edf0f5;
      font-size: 13px;
      text-align: left;
      vertical-align: middle;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

  /* ── Title row — sticky at top ────────────────────────────────────── */
  .cc-title-row th {
    background: #f0f3f8;
    color: #6f7b92;
    font-weight: 700;
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: 1px solid #d4daea;
  }

    .cc-title-row th.is-sortable {
      cursor: pointer;
      user-select: none;
    }

    .cc-title-row th.is-not-sortable {
      cursor: default;
    }

  .cc-th-inner {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
  }

  .cc-sort-icon {
    min-width: 12px;
    color: #8a94a8;
    font-size: 11px;
  }

  /* ── Resize handle ────────────────────────────────────────────────── */
  .cc-resizable-th {
    position: relative;
  }

  .cc-resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 5px;
    cursor: col-resize;
    z-index: 3;
    border-right: 2px solid transparent;
    transition: border-color 0.15s ease;
  }

    .cc-resize-handle:hover {
      border-right-color: #5a7fc4;
    }

  /* ── Filter row — sticky directly below title row ─────────────────── */
  .cc-filter-row th {
    background: #e8ecf4;
    position: sticky;
    top: 43px;
    z-index: 2;
    padding: 5px 8px;
    border-bottom: 2px solid #c6cfe3;
    cursor: default !important;
    user-select: auto !important;
    overflow: visible;
  }

  /* ── Filter controls ──────────────────────────────────────────────── */
  .cc-filter-input-wrap {
    display: flex;
    align-items: center;
    gap: 3px;
    width: 100%;
  }

  /* Operator picker now sits on the RIGHT — no position change needed,
   just ensure it stays flex-shrink:0 so it never squishes */
  .cc-op-wrap {
    position: relative;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #b8c3d8;
    border-radius: 4px;
    background: #dce2ef;
    color: #4a5675;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }

    .cc-op-wrap:hover {
      background: #c8d3e8;
      border-color: #5a7fc4;
      color: #1a2a4a;
    }

  .cc-op-icon {
    width: 12px;
    height: 12px;
    pointer-events: none;
  }

  .cc-op-select {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    font-size: 13px;
  }

  .cc-filter-input {
    flex: 1;
    min-width: 0;
    height: 24px;
    border: 1px solid #b8c3d8;
    border-radius: 4px;
    background: #ffffff;
    padding: 0 6px;
    font-size: 12px;
    color: #23314f;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

    .cc-filter-input:focus {
      border-color: #5a7fc4;
      box-shadow: 0 0 0 2px rgba(90, 127, 196, 0.15);
    }

    .cc-filter-input::placeholder {
      color: #aab2c4;
      font-style: italic;
      font-size: 11px;
    }

  input[type="date"].cc-filter-input,
  input[type="datetime-local"].cc-filter-input {
    min-width: 130px;
    height: 26px;
    font-size: 11px;
    padding: 0 4px;
  }

  .cc-filter-clear {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: none;
    border-radius: 50%;
    background: #b8c3d8;
    color: #3a4a6b;
    font-size: 9px;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: background 0.15s ease, color 0.15s ease;
  }

    .cc-filter-clear:hover {
      background: #8899bb;
      color: #fff;
    }

  .cc-filter-apply {
    flex-shrink: 0;
    height: 22px;
    padding: 0 7px;
    border: 1px solid #5a7fc4;
    border-radius: 4px;
    background: #5a7fc4;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease;
  }

    .cc-filter-apply:hover {
      background: #3f63a8;
      border-color: #3f63a8;
    }

  /* ── Body rows ────────────────────────────────────────────────────── */
  .cc-row-even {
    background: #fbfcfe;
  }

  .cc-row-odd {
    background: #f4f6fb;
  }

  .cc-table-row {
    cursor: pointer;
    transition: background 0.12s ease;
  }

    .cc-table-row:hover {
      background: #eaf0fb !important;
    }

  .cc-table-empty {
    text-align: center !important;
    padding: 32px 12px !important;
    color: #7b8397;
    font-size: 14px;
    font-style: italic;
  }

  /* ── Pagination bar — always visible at the bottom ────────────────── */
  .cc-pagination-bar {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    gap: 12px;
    border-top: 1px solid #e9edf3;
    background: #fafbfd;
  }

  .cc-pagination-info {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .cc-page-summary {
    color: #7b8397;
    font-size: 13px;
    font-weight: 500;
  }

  .cc-page-size-wrap {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #5c6780;
    font-size: 13px;
    font-weight: 600;
  }

  .cc-page-size-select {
    height: 34px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    padding: 0 10px;
    font-size: 13px;
    color: #23314f;
    outline: none;
  }

  .cc-pagination-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .cc-page-of {
    color: #7b8397;
    font-size: 13px;
    font-weight: 500;
  }

  .cc-page-btn {
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #22314f;
    height: 34px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

    .cc-page-btn:hover {
      background: #f4f7fc;
      border-color: #cfd8e8;
    }

    .cc-page-btn:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

  /* ── Checkbox col ─────────────────────────────────────────────────── */
  .cc-col-select {
    width: 48px;
    min-width: 48px;
    text-align: center;
  }

  .cc-checkbox-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

    .cc-checkbox-wrap input[type="checkbox"] {
      width: 15px;
      height: 15px;
      accent-color: #315db5;
      cursor: pointer;
    }

  /* ── Responsive ───────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .cc-table {
      min-width: 900px;
    }

    .cc-pagination-bar {
      flex-direction: column;
      align-items: flex-start;
    }

    .cc-table-toolbar-slot {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .cc-filter-input-wrap {
    display: flex;
    align-items: center;
    gap: 3px;
    width: 100%;
    overflow: hidden; /* add this */
  }

  /* ── Lock Extension button (type: lockExtension column) ───────────── */
  .cc-lock-ext-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 28px;
    padding: 0 4px;
    border-radius: 7px;
    border: 1px solid rgba(217, 119, 6, 0.35);
    background: rgba(255, 247, 237, 0.9);
    color: #b45309;
    font-size: 11.5px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

    .cc-lock-ext-btn:hover {
      background: rgba(253, 230, 138, 0.5);
      border-color: rgba(217, 119, 6, 0.6);
    }

    .cc-lock-ext-btn i {
      font-size: 12px;
      --fa-primary-color: #b45309;
      --fa-secondary-color: rgba(180, 83, 9, 0.3);
      --fa-secondary-opacity: 1;
    }
</style>
